// Subsidy / agency billing data client.
//
// Operational invoices remain the source of truth. This client deliberately
// contains no checkout or Stripe calls: all money-affecting writes are narrow,
// organization-scoped RPCs and agency handoff is recorded as a human action.

import { isSupabaseConfigured, supabase } from "./supabase";

function one(value) {
  return Array.isArray(value) ? value[0] || null : value || null;
}

async function rpc(name, args) {
  if (!isSupabaseConfigured) {
    throw new Error("Subsidy operations require a connected HoneyAcorn workspace.");
  }
  const { data, error } = await supabase.rpc(name, args);
  if (error) throw error;
  return data;
}

async function select(query) {
  if (!isSupabaseConfigured) return [];
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

export async function getSubsidyAgencyDashboard() {
  return (await rpc("rpc_subsidy_agency_dashboard", {})) || {};
}

export async function listSubsidyAgencies() {
  if (!isSupabaseConfigured) return [];
  return select(
    supabase
      .from("subsidy_agencies")
      .select("id, name, external_reference, billing_email, contact_name, contact_phone, active, created_at, updated_at")
      .order("active", { ascending: false })
      .order("name"),
  );
}

export async function listSubsidyAuthorizations() {
  if (!isSupabaseConfigured) return [];
  return select(
    supabase
      .from("subsidy_authorizations")
      .select(`
        id, agency_id, child_id, authorization_number, status, effective_on,
        expires_on, rate_unit, agency_responsibility_cents, parent_copay_cents,
        approved_schedule, attendance_requirements, notes, created_at, updated_at,
        agency:subsidy_agencies(id, name),
        child:children(id, family_id, first_name, last_name)
      `)
      .order("expires_on", { ascending: true, nullsFirst: false })
      .order("created_at", { ascending: false }),
  );
}

export async function listSubsidyClaims({ limit = 100 } = {}) {
  if (!isSupabaseConfigured) return [];
  return select(
    supabase
      .from("subsidy_claims")
      .select(`
        id, agency_id, claim_number, service_period_start, service_period_end,
        status, submitted_on, notes, created_at, updated_at,
        agency:subsidy_agencies(id, name),
        lines:subsidy_claim_lines(id, amount_claimed_cents)
      `)
      .order("service_period_end", { ascending: false })
      .limit(limit),
  );
}

export async function listSubsidyChildren() {
  if (!isSupabaseConfigured) return [];
  return select(
    supabase
      .from("children")
      .select("id, first_name, last_name, status, family:families(id, display_name)")
      .order("first_name")
      .order("last_name"),
  );
}

export async function listSubsidyEligibleInvoices() {
  if (!isSupabaseConfigured) return [];
  return select(
    supabase
      .from("invoices")
      .select(`
        id, invoice_number, title, issue_date, due_date, status,
        amount_total_cents, currency,
        billing_profile:billing_profiles(
          id,
          family:families(id, display_name)
        )
      `)
      .in("status", ["draft", "issued", "failed"])
      .order("due_date", { ascending: true, nullsFirst: false })
      .order("issue_date", { ascending: false })
      .limit(250),
  );
}

export async function getSubsidyClaimWorkspace(claimId) {
  if (!isSupabaseConfigured) return null;

  const { data: claim, error: claimError } = await supabase
    .from("subsidy_claims")
    .select(`
      id, agency_id, claim_number, service_period_start, service_period_end,
      status, submitted_on, notes, created_at, updated_at,
      agency:subsidy_agencies(id, name)
    `)
    .eq("id", claimId)
    .maybeSingle();
  if (claimError) throw claimError;
  if (!claim) return null;

  const lines = await select(
    supabase
      .from("subsidy_claim_lines")
      .select(`
        id, claim_id, authorization_id, child_id, invoice_id,
        invoice_responsibility_id, amount_claimed_cents, attendance_snapshot,
        created_at, updated_at,
        authorization:subsidy_authorizations(
          id, authorization_number, rate_unit, agency_responsibility_cents,
          parent_copay_cents
        ),
        child:children(id, first_name, last_name),
        invoice:invoices(
          id, invoice_number, title, amount_total_cents, currency, status,
          issue_date, due_date
        ),
        responsibility:invoice_responsibilities(id, amount_cents, payer_kind, status)
      `)
      .eq("claim_id", claimId)
      .order("created_at"),
  );

  const invoiceIds = [...new Set(lines.map((line) => line.invoice_id).filter(Boolean))];
  const relatedLines = invoiceIds.length
    ? await select(
        supabase
          .from("subsidy_claim_lines")
          .select("id, invoice_id, invoice_responsibility_id, amount_claimed_cents")
          .in("invoice_id", invoiceIds),
      )
    : [];
  const lineIds = relatedLines.map((line) => line.id);
  const [allocations, adjustments] = lineIds.length
    ? await Promise.all([
        select(
          supabase
            .from("subsidy_payment_allocations")
            .select(`
              id, claim_line_id, amount_cents, created_at,
              payment:subsidy_agency_payments(id, received_on, reference, note)
            `)
            .in("claim_line_id", lineIds)
            .order("created_at", { ascending: false }),
        ),
        select(
          supabase
            .from("subsidy_claim_adjustments")
            .select("id, claim_line_id, kind, amount_cents, reason, created_at")
            .in("claim_line_id", lineIds)
            .order("created_at", { ascending: false }),
        ),
      ])
    : [[], []];

  const responsibilities = invoiceIds.length
    ? await select(
        supabase
          .from("invoice_responsibilities")
          .select("id, invoice_id, amount_cents, payer_kind, status")
          .in("invoice_id", invoiceIds),
      )
    : [];
  const familyResponsibilityIds = responsibilities
    .filter((responsibility) => responsibility.payer_kind === "family" && responsibility.status === "active")
    .map((responsibility) => responsibility.id);
  const familyPayments = familyResponsibilityIds.length
    ? await select(
        supabase
          .from("subsidy_family_payments")
          .select("id, invoice_responsibility_id, amount_cents, received_on, reference, note, created_at")
          .in("invoice_responsibility_id", familyResponsibilityIds)
          .order("created_at", { ascending: false }),
      )
    : [];

  return {
    claim,
    lines,
    relatedLines,
    allocations,
    adjustments,
    responsibilities,
    familyPayments,
  };
}

export async function createSubsidyAgency({
  name,
  externalReference = null,
  billingEmail = null,
  contactName = null,
  contactPhone = null,
}) {
  return one(await rpc("rpc_create_subsidy_agency", {
    p_name: name,
    p_external_reference: externalReference || null,
    p_billing_email: billingEmail || null,
    p_contact_name: contactName || null,
    p_contact_phone: contactPhone || null,
  }));
}

export async function updateSubsidyAgency({
  agencyId,
  name,
  externalReference = null,
  billingEmail = null,
  contactName = null,
  contactPhone = null,
  active = true,
}) {
  return one(await rpc("rpc_update_subsidy_agency", {
    p_agency_id: agencyId,
    p_name: name,
    p_external_reference: externalReference || null,
    p_billing_email: billingEmail || null,
    p_contact_name: contactName || null,
    p_contact_phone: contactPhone || null,
    p_active: active,
  }));
}

export async function createSubsidyAuthorization({
  agencyId,
  childId,
  authorizationNumber,
  effectiveOn,
  expiresOn = null,
  rateUnit = "monthly",
  agencyResponsibilityCents = 0,
  parentCopayCents = 0,
  approvedSchedule = {},
  attendanceRequirements = {},
  notes = null,
}) {
  return one(await rpc("rpc_create_subsidy_authorization", {
    p_agency_id: agencyId,
    p_child_id: childId,
    p_authorization_number: authorizationNumber,
    p_effective_on: effectiveOn,
    p_expires_on: expiresOn || null,
    p_rate_unit: rateUnit,
    p_agency_responsibility_cents: agencyResponsibilityCents,
    p_parent_copay_cents: parentCopayCents,
    p_approved_schedule: approvedSchedule || {},
    p_attendance_requirements: attendanceRequirements || {},
    p_notes: notes || null,
  }));
}

export async function updateSubsidyAuthorizationStatus({ authorizationId, status }) {
  return one(await rpc("rpc_update_subsidy_authorization_status", {
    p_authorization_id: authorizationId,
    p_status: status,
  }));
}

export async function createSubsidyClaim({
  agencyId,
  servicePeriodStart,
  servicePeriodEnd,
  claimNumber = null,
  notes = null,
}) {
  return one(await rpc("rpc_create_subsidy_claim", {
    p_agency_id: agencyId,
    p_service_period_start: servicePeriodStart,
    p_service_period_end: servicePeriodEnd,
    p_claim_number: claimNumber || null,
    p_notes: notes || null,
  }));
}

export async function addSubsidyClaimLine({
  claimId,
  authorizationId,
  invoiceId,
  amountClaimedCents = null,
}) {
  return one(await rpc("rpc_add_subsidy_claim_line", {
    p_claim_id: claimId,
    p_authorization_id: authorizationId,
    p_invoice_id: invoiceId,
    p_amount_claimed_cents: amountClaimedCents || null,
  }));
}

export async function removeSubsidyClaimLine(claimLineId) {
  return rpc("rpc_remove_subsidy_claim_line", { p_claim_line_id: claimLineId });
}

export async function submitSubsidyClaim(claimId) {
  return one(await rpc("rpc_submit_subsidy_claim", { p_claim_id: claimId }));
}

export async function recordSubsidyAgencyPayment({
  claimLineId,
  amountCents,
  receivedOn = null,
  reference = null,
  note = null,
  idempotencyKey,
}) {
  return one(await rpc("rpc_record_subsidy_agency_payment", {
    p_claim_line_id: claimLineId,
    p_amount_cents: amountCents,
    p_received_on: receivedOn || null,
    p_reference: reference || null,
    p_note: note || null,
    p_idempotency_key: idempotencyKey,
  }));
}

export async function recordSubsidyClaimAdjustment({
  claimLineId,
  kind,
  amountCents,
  reason,
  idempotencyKey,
}) {
  return one(await rpc("rpc_record_subsidy_claim_adjustment", {
    p_claim_line_id: claimLineId,
    p_kind: kind,
    p_amount_cents: amountCents,
    p_reason: reason,
    p_idempotency_key: idempotencyKey,
  }));
}

export async function recordSubsidyFamilyPayment({
  invoiceId,
  amountCents,
  receivedOn = null,
  reference = null,
  note = null,
  idempotencyKey,
}) {
  return one(await rpc("rpc_record_subsidy_family_payment", {
    p_invoice_id: invoiceId,
    p_amount_cents: amountCents,
    p_received_on: receivedOn || null,
    p_reference: reference || null,
    p_note: note || null,
    p_idempotency_key: idempotencyKey,
  }));
}

export async function voidSubsidyClaim({ claimId, reason }) {
  return one(await rpc("rpc_void_subsidy_claim", {
    p_claim_id: claimId,
    p_reason: reason,
  }));
}

/** Parent-safe coverage projection. The RPC intentionally omits agency and
 * authorization details, so this function can safely enrich mobile billing. */
export async function getParentSubsidyInvoiceSummaries() {
  return (await rpc("rpc_parent_subsidy_invoice_summaries", {})) || [];
}
